<script setup lang="ts">
import type { Money } from '@taler/domain'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import AmountInput from '@/app/components/AmountInput.vue'
import type { RowView } from '@/features/entries'
import type {
  CrossLedgerMovementDraft,
  CrossLedgerMovementMatch,
} from '../composables/use-cross-ledger-movement-extension.ts'
import { needsTargetAmount } from '../composables/use-cross-ledger-movement-extension.ts'

const props = defineProps<{
  match: CrossLedgerMovementMatch
  draft: CrossLedgerMovementDraft
  view: RowView
}>()
const emit = defineEmits<{ 'update:draft': [CrossLedgerMovementDraft] }>()

const { t, locale } = useI18n()
const currentLocale = computed(() => locale.value as 'en' | 'hu')
const showTargetAmount = computed(() => needsTargetAmount(props.view))
const targetCurrency = computed(() => props.view.counters[0]?.currency)

function update(targetAmount: Money | null): void {
  emit('update:draft', { targetAmount })
}
</script>

<template>
  <div v-if="showTargetAmount" class="flex flex-wrap items-center gap-[var(--space-cell)]">
    <div
      class="flex items-center gap-[var(--space-inline)] text-[length:var(--text-step-2)] text-[color:var(--color-text)]"
    >
      <span aria-hidden="true">{{ t('transfers.extension.targetAmountLabel') }}</span>
      <div class="w-40">
        <AmountInput
          :model-value="draft.targetAmount"
          :currency="targetCurrency"
          :locale="currentLocale"
          :show-total="false"
          :aria-label="t('transfers.extension.targetAmountAriaLabel', { currency: targetCurrency })"
          @update:model-value="update"
        />
      </div>
    </div>
  </div>
</template>
